import { useState, useEffect } from 'react';
import { ZapDialog } from '@/components/ZapDialog';
import { useDirectZap, useZaps, ZapSuccessInfo } from '@/hooks/useZaps';
import { useWallet } from '@/hooks/useWallet';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useConfetti } from '@/hooks/useConfetti';
import { Zap, Copy, Check, ExternalLink, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import type { Event } from 'nostr-tools';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import QRCode from 'qrcode';

interface ZapButtonPropsBase {
  className?: string;
  showCount?: boolean;
  zapData?: { count: number; totalSats: number; isLoading?: boolean };
}

// Event-based zapping (for posts, etc.)
interface EventZapButtonProps extends ZapButtonPropsBase {
  target: Event;
  pubkey?: never;
  amount?: never;
  comment?: never;
  variant?: never;
  children?: never;
  onSuccess?: never;
}

// Direct pubkey zapping (for tipping creators)
interface DirectZapButtonProps extends ZapButtonPropsBase {
  pubkey: string;
  amount: number;
  comment?: string;
  variant?: ButtonHTMLAttributes<HTMLButtonElement>['type'] | 'default' | 'secondary' | 'outline' | 'ghost';
  children?: ReactNode;
  onSuccess?: () => void;
  disabled?: boolean;
  target?: never;
}

type ZapButtonProps = EventZapButtonProps | DirectZapButtonProps;

export function ZapButton(props: ZapButtonProps) {
  // Direct pubkey zapping mode
  if ('pubkey' in props && props.pubkey) {
    return <DirectZapButton {...props as DirectZapButtonProps} />;
  }

  // Event-based zapping mode
  return <EventZapButton {...props as EventZapButtonProps} />;
}

function DirectZapButton({
  pubkey,
  amount,
  comment = '',
  variant = 'default',
  className,
  children,
  onSuccess,
  disabled,
}: DirectZapButtonProps) {
  const { user } = useCurrentUser();
  const { data: author } = useAuthor(pubkey);
  const { webln, activeNWC } = useWallet();
  const { fireZapConfetti } = useConfetti();
  const queryClient = useQueryClient();
  const { zap, isZapping, invoice, setInvoice, zapSuccess, resetZapSuccess } = useDirectZap(pubkey, webln, activeNWC, (info) => {
    // Fire confetti on successful zap
    fireZapConfetti();
    // Invalidate zaps query to refresh the list immediately
    queryClient.invalidateQueries({ queryKey: ['zaps', pubkey] });
  });
  const { toast } = useToast();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const recipientName = author?.metadata?.display_name || author?.metadata?.name;

  // Generate QR code when invoice is available
  useEffect(() => {
    let isCancelled = false;

    const generateQR = async () => {
      if (!invoice) {
        setQrCodeUrl('');
        return;
      }

      try {
        const url = await QRCode.toDataURL(invoice.toUpperCase(), {
          width: 512,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });

        if (!isCancelled) {
          setQrCodeUrl(url);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to generate QR code:', err);
        }
      }
    };

    generateQR();

    return () => {
      isCancelled = true;
    };
  }, [invoice]);

  // Show modal when invoice is available or zap succeeds
  useEffect(() => {
    if (invoice || zapSuccess) {
      setShowPaymentModal(true);
    }
  }, [invoice, zapSuccess]);

  // Don't show zap button if user is not logged in, is the author, or author has no lightning address
  if (!user || user.pubkey === pubkey || (!author?.metadata?.lud16 && !author?.metadata?.lud06)) {
    return null;
  }

  const handleClick = () => {
    zap(amount, comment);
  };

  const handleCopy = async () => {
    if (invoice) {
      await navigator.clipboard.writeText(invoice);
      setCopied(true);
      toast({
        title: 'Invoice copied',
        description: 'Lightning invoice copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openInWallet = () => {
    if (invoice) {
      const lightningUrl = `lightning:${invoice}`;
      window.open(lightningUrl, '_blank');
    }
  };

  const handleClose = () => {
    setShowPaymentModal(false);
    setInvoice(null);
    setQrCodeUrl('');
    setCopied(false);
    // Only call onSuccess and reset when closing after a successful zap
    if (zapSuccess) {
      onSuccess?.();
      resetZapSuccess();
    }
  };

  return (
    <>
      <Button
        variant={variant as 'default' | 'secondary' | 'outline' | 'ghost'}
        className={className}
        onClick={handleClick}
        disabled={disabled || isZapping}
      >
        {isZapping ? (
          <>
            <Zap className="mr-2 h-5 w-5 animate-pulse" />
            Creating invoice...
          </>
        ) : (
          children || (
            <>
              <Zap className="mr-2 h-5 w-5" />
              Zap {amount.toLocaleString()} sats
            </>
          )
        )}
      </Button>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-md rounded-3xl border-0 shadow-2xl" aria-describedby="payment-description">
          {zapSuccess ? (
            // Success View
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
                <div className="relative bg-gradient-to-br from-green-500 to-emerald-600 p-5 rounded-full shadow-lg shadow-green-500/30">
                  <Check className="h-10 w-10 text-white" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-center mb-2">
                Zap Sent! ⚡
              </h3>

              <p className="text-muted-foreground text-center mb-2">
                You sent <span className="font-semibold text-foreground">{zapSuccess.amount.toLocaleString()} sats</span>
                {recipientName && (
                  <> to <span className="font-semibold text-foreground">{recipientName}</span></>
                )}
              </p>

              {zapSuccess.comment && (
                <p className="text-sm text-muted-foreground text-center italic mb-6 max-w-xs">
                  "{zapSuccess.comment}"
                </p>
              )}

              <div className="flex items-center gap-2 text-primary mb-8">
                <PartyPopper className="h-5 w-5" />
                <span className="text-sm font-medium">Thanks for supporting!</span>
                <PartyPopper className="h-5 w-5" />
              </div>

              <Button
                onClick={handleClose}
                className="w-full max-w-xs"
                size="lg"
              >
                Done
              </Button>
            </div>
          ) : (
            // Payment View
            <>
              <DialogHeader className="text-center">
                <DialogTitle className="text-xl font-semibold">Lightning Payment</DialogTitle>
                <DialogDescription id="payment-description" className="text-sm">
                  Pay with Bitcoin Lightning Network
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col items-center">
                {/* Amount */}
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-primary">{amount.toLocaleString()} sats</div>
                </div>

                {/* QR Code */}
                <Card className="p-3 mb-6">
                  <CardContent className="p-0">
                    {qrCodeUrl ? (
                      <img
                        src={qrCodeUrl}
                        alt="Lightning Invoice QR Code"
                        className="w-64 h-64 object-contain"
                      />
                    ) : (
                      <div className="w-64 h-64 bg-muted animate-pulse rounded" />
                    )}
                  </CardContent>
                </Card>

                {/* Invoice input */}
                <div className="w-full space-y-2 mb-6">
                  <Label htmlFor="invoice" className="text-sm font-medium">Lightning Invoice</Label>
                  <div className="flex gap-2">
                    <Input
                      id="invoice"
                      value={invoice || ''}
                      readOnly
                      className="font-mono text-xs flex-1 overflow-hidden text-ellipsis"
                      onClick={(e) => e.currentTarget.select()}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopy}
                      className="shrink-0"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="w-full space-y-3">
                  <Button
                    variant="outline"
                    onClick={openInWallet}
                    className="w-full h-12 rounded-xl"
                    size="lg"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in Wallet
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Scan the QR code or copy the invoice to pay with any Lightning wallet.
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function EventZapButton({
  target,
  className = "text-xs ml-1",
  showCount = true,
  zapData: externalZapData
}: EventZapButtonProps) {
  const { user } = useCurrentUser();
  const { data: author } = useAuthor(target?.pubkey || '');
  const { webln, activeNWC } = useWallet();

  // Only fetch data if not provided externally
  const { totalSats: fetchedTotalSats, isLoading } = useZaps(
    externalZapData ? [] : target ?? [], // Empty array prevents fetching if external data provided
    webln,
    activeNWC
  );

  // Don't show zap button if user is not logged in, is the author, or author has no lightning address
  if (!user || !target || user.pubkey === target.pubkey || (!author?.metadata?.lud16 && !author?.metadata?.lud06)) {
    return null;
  }

  // Use external data if provided, otherwise use fetched data
  const totalSats = externalZapData?.totalSats ?? fetchedTotalSats;
  const showLoading = externalZapData?.isLoading || isLoading;

  return (
    <ZapDialog target={target}>
      <div className={`flex items-center gap-1 ${className}`}>
        <Zap className="h-4 w-4" />
        <span className="text-xs">
          {showLoading ? (
            '...'
          ) : showCount && totalSats > 0 ? (
            `${totalSats.toLocaleString()}`
          ) : (
            'Zap'
          )}
        </span>
      </div>
    </ZapDialog>
  );
}
